import { useState, useRef, createContext, useContext } from "react";
import {
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Paper,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

// Global Context to track active hover path across nested levels
const MenuHoverContext = createContext({
    activeHoverPath: [],
    setActiveHoverPath: () => {},
    clearHoverTimeout: () => {},
    startHoverTimeout: () => {}
});

export default function SidebarMenu({
    items,
    level = 0,
    isCollapsed = false,
    parentPath = [],
    onCloseParentPopover
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navigate = useNavigate();
    const location = useLocation();

    const [anchorEls, setAnchorEls] = useState({});
    const timeoutRef = useRef(null);

    // Parent/Root context state management
    const parentContext = useContext(MenuHoverContext);
    const [activeHoverPath, setActiveHoverPath] = useState([]);

    const isRoot = level === 0;

    const startHoverTimeout = () => {
        if (isRoot) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setActiveHoverPath([]);
                setAnchorEls({});
            }, 200); // Buffer time to move cursor between list item and popover
        } else {
            parentContext.startHoverTimeout();
        }
    };

    const clearHoverTimeout = () => {
        if (isRoot) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        } else {
            parentContext.clearHoverTimeout();
        }
    };

    const currentHoverPath = isRoot
        ? activeHoverPath
        : parentContext.activeHoverPath;
    const currentSetActiveHoverPath = isRoot
        ? setActiveHoverPath
        : parentContext.setActiveHoverPath;

    const isPathActive = (item) => {
        if (item.path && location.pathname === item.path) return true;
        if (item.children?.length) {
            return item.children.some((child) => isPathActive(child));
        }
        return false;
    };

    const handleOpenPopover = (targetElement, item, currentItemPath) => {
        clearHoverTimeout();
        currentSetActiveHoverPath(currentItemPath);
        setAnchorEls((prev) => ({
            ...prev,
            [item.id]: targetElement
        }));
    };

    const handleCloseAll = () => {
        clearHoverTimeout();
        if (isRoot) {
            setActiveHoverPath([]);
            setAnchorEls({});
        } else {
            currentSetActiveHoverPath([]);
        }
        if (onCloseParentPopover) {
            onCloseParentPopover();
        }
    };

    const content = (
        <List disablePadding sx={{ width: "100%" }}>
            {items.map((item) => {
                const Icon = item.icon;
                const hasChildren = item.children && item.children.length > 0;
                const shouldShowPopover =
                    hasChildren || (isCollapsed && level === 0);

                const currentItemPath = [...parentPath, item.id];
                const anchorEl = anchorEls[item.id];

                // Keep popover open if this item is inside the active hover chain
                const isOpen =
                    Boolean(anchorEl) && currentHoverPath.includes(item.id);
                const active = isPathActive(item);

                const handleMouseEnter = (event) => {
                    if (shouldShowPopover && event.pointerType !== "touch") {
                        handleOpenPopover(
                            event.currentTarget,
                            item,
                            currentItemPath
                        );
                    } else if (
                        !shouldShowPopover &&
                        event.pointerType !== "touch"
                    ) {
                        clearHoverTimeout();
                        currentSetActiveHoverPath(currentItemPath);
                    }
                };

                const handleMouseLeave = () => {
                    startHoverTimeout();
                };

                const handleClick = (event) => {
                    if (shouldShowPopover) {
                        if (isOpen) {
                            currentSetActiveHoverPath(parentPath);
                        } else {
                            handleOpenPopover(
                                event.currentTarget,
                                item,
                                currentItemPath
                            );
                        }
                    } else {
                        if (item.path) {
                            navigate(item.path);
                        }
                        handleCloseAll();
                    }
                };

                const menuItemContent = (
                    <ListItemButton
                        selected={active}
                        onClick={handleClick}
                        sx={{
                            py: level === 0 ? 1.2 : "2px",
                            px: isCollapsed && level === 0 ? 0 : 2,
                            justifyContent:
                                isCollapsed && level === 0
                                    ? "center"
                                    : level > 0
                                      ? "space-between"
                                      : "initial",
                            borderLeft:
                                active && level === 0
                                    ? "4px solid"
                                    : "4px solid transparent"
                            //borderColor: "primary.main"
                        }}
                    >
                        {Icon && (
                            <ListItemIcon
                                sx={{
                                    minWidth:
                                        isCollapsed && level === 0
                                            ? "auto"
                                            : 36,
                                    justifyContent: "center",
                                    color: active ? "primary.main" : "inherit"
                                }}
                            >
                                <Icon fontSize="small" />
                            </ListItemIcon>
                        )}

                        {(!isCollapsed || level > 0) && (
                            <ListItemText
                                primary={item.name}
                                primaryTypographyProps={{
                                    fontSize: "0.875rem",
                                    fontWeight: active ? 600 : 400
                                }}
                            />
                        )}

                        {hasChildren && (!isCollapsed || level > 0) && (
                            <ChevronRight
                                sx={{
                                    fontSize: 18,
                                    color: "action.active",
                                    ml: 1
                                }}
                            />
                        )}
                    </ListItemButton>
                );

                return (
                    <div
                        key={item.id}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        /*style={
                            item.break
                                ? {
                                      borderTop: "1px solid",
                                      borderColor: "divider"
                                  }
                                : {}
                        }*/
                    >
                        {item.break && (
                            <div
                                style={{
                                    height: "1px",
                                    width: "100%",
                                    backgroundColor: "rgba(0, 0, 0, 0.1)", // subtle gray line
                                    margin: "4px 0", // spacing above and below
                                    border: "none"
                                }}
                            />
                        )}
                        {isCollapsed && level === 0 && !hasChildren ? (
                            <Tooltip title={item.name} placement="right" arrow>
                                {menuItemContent}
                            </Tooltip>
                        ) : (
                            menuItemContent
                        )}

                        {/* Flyout Popover (Always opens to the Right) */}
                        {shouldShowPopover && (
                            <Popover
                                open={isOpen}
                                anchorEl={anchorEl}
                                onClose={() => {
                                    currentSetActiveHoverPath(parentPath);
                                }}
                                anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "right"
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left"
                                }}
                                disableRestoreFocus
                                sx={{ pointerEvents: "none" }}
                                slotProps={{
                                    paper: {
                                        elevation: 8,
                                        onMouseEnter: () => {
                                            clearHoverTimeout();
                                            currentSetActiveHoverPath(
                                                currentItemPath
                                            );
                                        },
                                        onMouseLeave: () => {
                                            startHoverTimeout();
                                        },
                                        sx: {
                                            pointerEvents: "auto",
                                            // 1. Overlap significantly to the left over the sidebar item:
                                            ml: isCollapsed ? ".5" : "-50px", // Adjust between -100px and -150px to get your exact overlap preference

                                            // 2. Adjust vertical position down slightly if you want it centered with the parent:
                                            mt: 1,
                                            minWidth: 220,
                                            maxWidth: 280,
                                            borderRadius: 2,
                                            py: 0.5
                                        }
                                    }
                                }}
                            >
                                <Paper elevation={0}>
                                    {isCollapsed && level === 0 && (
                                        <ListItemButton
                                            onClick={() => {
                                                if (item.path)
                                                    navigate(item.path);
                                                handleCloseAll();
                                            }}
                                            sx={{
                                                borderBottom: "1px solid",
                                                borderColor: "divider",
                                                py: 1
                                            }}
                                        >
                                            <ListItemText
                                                primary={item.name}
                                                primaryTypographyProps={{
                                                    fontWeight: 700,
                                                    fontSize: "0.9rem",
                                                    color: "primary.main"
                                                }}
                                            />
                                        </ListItemButton>
                                    )}

                                    {hasChildren && (
                                        <SidebarMenu
                                            items={item.children}
                                            level={level + 1}
                                            isCollapsed={false}
                                            parentPath={currentItemPath}
                                            onCloseParentPopover={
                                                handleCloseAll
                                            }
                                        />
                                    )}
                                </Paper>
                            </Popover>
                        )}
                    </div>
                );
            })}
        </List>
    );

    if (isRoot) {
        return (
            <MenuHoverContext.Provider
                value={{
                    activeHoverPath,
                    setActiveHoverPath,
                    clearHoverTimeout,
                    startHoverTimeout
                }}
            >
                {content}
            </MenuHoverContext.Provider>
        );
    }

    return content;
}
